/**
 * Central configuration for Flex Office User Kiosk Mode.
 * Kept as plain constants (no new dependency) so timeouts/allowlists are
 * defined in exactly one place.
 */

// sessionStorage key that marks the current tab as an active kiosk session.
// sessionStorage (not localStorage) is used deliberately: it never survives
// a full browser/tab restart, which limits how long a stale kiosk session
// can linger on a shared device.
export const KIOSK_SESSION_FLAG_KEY = "kioskSessionActive";
export const KIOSK_SESSION_STARTED_AT_KEY = "kioskSessionStartedAt";

// Set once the entry PIN (below) has been verified for this browser tab.
// sessionStorage for the same reason as the flag above — a fresh tab/device
// restart must always re-prompt. Kept distinct from KIOSK_SESSION_FLAG_KEY,
// which only turns on after a guest actually logs in/registers at
// /kiosk/login — the PIN gate covers the pre-login welcome/login/register
// screens too, which have no guest session yet.
export const KIOSK_PIN_VERIFIED_KEY = "kioskPinVerified";

// Idle timeout before the kiosk warns the current guest, and before it
// force-ends the session and clears data. Deliberately much shorter than the
// normal site's 15 minute IdleTimerContainer (see components/IdleTimerContainer.jsx)
// because this device is shared by walk-in strangers, not a single account holder.
// Industry-standard self-service kiosks auto-logout within ~30-60s of
// inactivity total (warning + response window included).
export const KIOSK_IDLE_WARNING_MS = 30 * 1000; // show warning after 30s idle
export const KIOSK_IDLE_LOGOUT_COUNTDOWN_SECONDS = 15; // then 15s to respond (45s total)
export const KIOSK_IDLE_TIMEOUT_MS = KIOSK_IDLE_WARNING_MS
  + KIOSK_IDLE_LOGOUT_COUNTDOWN_SECONDS * 1000;

// Routes the kiosk is allowed to be on once a kiosk session is active.
// Anything outside this allowlist is treated as "escaped" navigation and is
// redirected back to /kiosk. This intentionally includes the *existing*,
// unmodified Flex Office booking/payment/receipt routes so kiosk mode can
// reuse them without any changes to those pages.
export const KIOSK_ALLOWED_PATH_PREFIXES = [
  "/kiosk",
  "/flex-home",
  "/selected-product-details",
  "/view-service",
  "/review-schedule",
  "/confirm-book",
  "/my-bookings",
  "/my-orders",
  "/my-invoice",
  "/reciept/view",
  "/receipt/view",
  "/policy",
  "/book-space",
  "/type-of-spaces",
  "/book-schedule",
  "/choice",
  "/sign-up",
  "/sign-in",
];

export const isKioskAllowedPath = (pathname = "") => {
  const normalized = String(pathname || "/").toLowerCase();
  return KIOSK_ALLOWED_PATH_PREFIXES.some((prefix) => (
    normalized === prefix || normalized.startsWith(`${prefix}/`) || normalized.startsWith(`${prefix}?`)
  ));
};

// Payment gateways (Stripe/Razorpay) redirect back to this site's *host*,
// not necessarily a kiosk-allowed path (see ConfirmBook/index.jsx submitPayment,
// which sends window.location.host as the gateway return target). App.jsx's
// existing top-level query-param handling (responseToken / razorpay_payment_link_status)
// then takes over regardless of path. Kiosk navigation restriction must not
// race with that handoff, so it is skipped for exactly one navigation when
// these query params are present.
export const isPaymentGatewayCallback = (search = "") => (
  /[?&](responseToken|razorpay_payment_link_status)=/.test(String(search || ""))
);

// Roles allowed to use the kiosk self-service flow. Any other role
// (ADMIN/SUPER_ADMIN/INSTRUCTOR/EMPLOYEE) must never be signed into a public
// kiosk device, so kiosk login rejects those roles outright.
export const KIOSK_ALLOWED_ROLES = ["CUSTOMER"];

export const KIOSK_HOME_ROUTE = "/kiosk";

// Lightweight PIN gates for entering/exiting kiosk mode. These are a
// convenience gate against casual guest taps and accidental shortcut presses
// only — a PIN embedded in a frontend bundle is not secret, and this is
// explicitly NOT a substitute for real staff/admin authentication. Fixed
// (not env-configurable) so the entry/exit codes are guaranteed consistent
// across every deployment.
export const KIOSK_ENTRY_PIN = "1010";
export const KIOSK_EXIT_PIN = "0000";
